import type { ReactElement } from "react";

import JsonLd from "../../../../helpers/JsonLD";
import { MetaData } from "../../../../helpers/MetaData";
import { SocialPreviewImage, socialPreviews } from "../../../../utils/socialPreviews";
import type { ResourceArticle, ResourceCategory } from "./data/resourceArticles";

type PublishedResourceCategory = Extract<ResourceCategory, "newborn" | "baby" | "maternity" | "family">;

interface ResourceCategorySeo {
  description: string;
  image: SocialPreviewImage;
  label: string;
  title: string;
}

const siteUrl = "https://alenalobacheva.com";
const websiteId = `${siteUrl}/#website`;
const publisherId = `${siteUrl}/#localbusiness`;

const categorySeo: Record<PublishedResourceCategory, ResourceCategorySeo> = {
  newborn: {
    label: "Newborn",
    title: "Newborn Photography Guides | Charlotte, NC",
    description:
      "Newborn photography guides covering the best age for photos, what happens during a session and how Charlotte families can prepare.",
    image: socialPreviews.resources.hub.newborn,
  },
  baby: {
    label: "Baby",
    title: "Baby Photography Guides | Charlotte, NC",
    description:
      "Baby photography guides for choosing the right milestone, understanding the session and preparing for beautiful baby photos in Charlotte, NC.",
    image: socialPreviews.resources.hub.baby,
  },
  maternity: {
    label: "Maternity",
    title: "Maternity Photography Guides | Charlotte, NC",
    description:
      "Maternity photography guides about timing, outfits and session preparation from Charlotte maternity photographer Alena Lobacheva.",
    image: socialPreviews.resources.hub.maternity,
  },
  family: {
    label: "Family",
    title: "Family Photography Guides | Charlotte, NC",
    description:
      "Family photography guides for choosing the best light, coordinating outfits and preparing children for a relaxed Charlotte family session.",
    image: socialPreviews.resources.hub.family,
  },
};

const createBreadcrumb = (items: Array<{ name: string; url: string }>): object => ({
  "@type": "BreadcrumbList",
  itemListElement: items.map((item, index) => ({
    "@type": "ListItem",
    position: index + 1,
    name: item.name,
    item: item.url,
  })),
});

export const ResourceIndexSeo = (): ReactElement => {
  const canonicalUrl = `${siteUrl}/resources/`;
  const title = "Photography Guides for Families | Charlotte, NC";
  const description =
    "Explore newborn, maternity, baby and family photography guides with practical timing, wardrobe and session-preparation advice for Charlotte families.";
  const image = socialPreviews.resources.index;
  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "CollectionPage",
        "@id": `${canonicalUrl}#webpage`,
        url: canonicalUrl,
        name: title,
        description,
        image: image.imageOG,
        isPartOf: { "@id": websiteId },
      },
      createBreadcrumb([
        { name: "Home", url: `${siteUrl}/` },
        { name: "Photography Resources", url: canonicalUrl },
      ]),
    ],
  };

  return (
    <>
      <MetaData
        canonicalLink={canonicalUrl}
        description={description}
        descriptionOG={description}
        imageAltOG="Newborn photography resources for Charlotte families"
        {...image}
        title={title}
        titleOG={title}
      />
      <JsonLd data={schema} />
    </>
  );
};

export const ResourceHubSeo = ({ category }: { category: PublishedResourceCategory }): ReactElement => {
  const config = categorySeo[category];
  const canonicalUrl = `${siteUrl}/resources/${category}/`;
  const image = config.image;
  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "CollectionPage",
        "@id": `${canonicalUrl}#webpage`,
        url: canonicalUrl,
        name: config.title,
        description: config.description,
        image: image.imageOG,
        isPartOf: { "@id": websiteId },
      },
      createBreadcrumb([
        { name: "Home", url: `${siteUrl}/` },
        { name: "Photography Resources", url: `${siteUrl}/resources/` },
        { name: `${config.label} Photography Guides`, url: canonicalUrl },
      ]),
    ],
  };

  return (
    <>
      <MetaData
        canonicalLink={canonicalUrl}
        description={config.description}
        descriptionOG={config.description}
        imageAltOG={`${config.label} photography guides in Charlotte, North Carolina`}
        {...image}
        title={config.title}
        titleOG={config.title}
      />
      <JsonLd data={schema} />
    </>
  );
};

export const ResourceArticleSeo = ({ article }: { article: ResourceArticle }): ReactElement => {
  const category = article.category as PublishedResourceCategory;
  const config = categorySeo[category];
  const canonicalUrl = `${siteUrl}/resources/${article.category}/${article.slug}/`;
  const title = article.seoTitle ?? `${article.title} | Charlotte, NC`;
  const description =
    article.seoDescription ?? `${article.description} Advice from Charlotte photographer Alena Lobacheva.`;
  const image =
    socialPreviews.resources.article[article.slug as keyof typeof socialPreviews.resources.article] ?? config.image;
  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Article",
        "@id": `${canonicalUrl}#article`,
        headline: article.title,
        description,
        image: [image.imageOG],
        datePublished: article.publishedAt,
        dateModified: article.updatedAt ?? article.publishedAt,
        articleSection: `${config.label} Photography`,
        inLanguage: "en-US",
        author: {
          "@type": "Person",
          name: "Alena Lobacheva",
          url: `${siteUrl}/about/`,
        },
        publisher: { "@id": publisherId },
        mainEntityOfPage: { "@type": "WebPage", "@id": canonicalUrl },
      },
      createBreadcrumb([
        { name: "Home", url: `${siteUrl}/` },
        { name: "Photography Resources", url: `${siteUrl}/resources/` },
        { name: `${config.label} Photography Guides`, url: `${siteUrl}/resources/${category}/` },
        { name: article.title, url: canonicalUrl },
      ]),
    ],
  };

  return (
    <>
      <MetaData
        canonicalLink={canonicalUrl}
        description={description}
        descriptionOG={description}
        imageAltOG={article.imageAlt ?? `${article.title} — ${config.label.toLowerCase()} photography guide`}
        {...image}
        title={title}
        titleOG={article.title}
        type="article"
      />
      <JsonLd data={schema} />
    </>
  );
};
